import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Megaphone, Loader2, Check, X, Eye, EyeOff, Calendar, Clock,
  ExternalLink, Palette, Upload, Archive, ChevronDown, Trash2, Video
} from 'lucide-react';
import { toast } from 'sonner';
import { SponsorInfo } from './SponsorDisplay';
import { Calendar as CalendarUI } from '../components/ui/calendar';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  pending: 'bg-amber-400/10 text-amber-400',
  approved: 'bg-blue-400/10 text-blue-400',
  published: 'bg-green-400/10 text-green-400',
  rejected: 'bg-red-400/10 text-red-400',
  archived: 'bg-white/5 text-white/30',
};

const SponsorAdmin = ({ token }) => {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [schedStart, setSchedStart] = useState('');
  const [schedEnd, setSchedEnd] = useState('');

  // Default video + metadata state
  const [defaultPromo, setDefaultPromo] = useState({ video_url: null, sponsor_name: '', headline: '', description: '', cta_text: '', website_url: '', contact_info: '' });
  const [dvLoading, setDvLoading] = useState(false);
  const [metaSaving, setMetaSaving] = useState(false);
  const [pendingVideoFile, setPendingVideoFile] = useState(null);
  const [pendingVideoPreview, setPendingVideoPreview] = useState(null);
  const dvFileRef = useRef(null);

  // Booked dates calendar
  const [bookedDates, setBookedDates] = useState([]);
  const [calMonth, setCalMonth] = useState(new Date());

  const fetch_ = useCallback(() => {
    setLoading(true);
    fetch(`${API}/api/admin/sponsor/submissions`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { setSubs(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  const fetchDefaultPromo = useCallback(() => {
    fetch(`${API}/api/sponsor/default-video`)
      .then(r => r.json())
      .then(d => setDefaultPromo({
        video_url: d?.video_url || null,
        sponsor_name: d?.sponsor_name || '',
        headline: d?.headline || '',
        description: d?.description || '',
        cta_text: d?.cta_text || '',
        website_url: d?.website_url || '',
        contact_info: d?.contact_info || '',
      }))
      .catch(() => {});
  }, []);

  useEffect(() => { fetch_(); fetchDefaultPromo(); }, [fetch_, fetchDefaultPromo]);

  // Fetch booked dates for calendar
  useEffect(() => {
    fetch(`${API}/api/admin/sponsor/booked-dates`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setBookedDates(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [token, subs]);

  const action = async (subId, act, body = null) => {
    const method = act === 'edit' ? 'PUT' : 'POST';
    const url = act === 'edit'
      ? `${API}/api/admin/sponsor/${subId}`
      : `${API}/api/admin/sponsor/${subId}/${act}`;
    const opts = { method, headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } };
    if (body) opts.body = JSON.stringify(body);
    const res = await fetch(url, opts);
    if (res.ok) { toast.success(`Sponsor ${act}d`); fetch_(); }
    else { const d = await res.json().catch(() => ({})); toast.error(d.detail || 'Action failed'); }
  };

  const handlePublish = (subId) => {
    if (!schedStart || !schedEnd) { toast.error('Set start and end dates'); return; }
    action(subId, 'publish', { scheduled_start_at: schedStart, scheduled_end_at: schedEnd });
  };

  const handleDefaultVideoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('video/')) { toast.error('Only video files allowed'); return; }
    if (file.size > 100 * 1024 * 1024) { toast.error('File too large (max 100MB)'); return; }
    // Store file for later upload, create local preview
    setPendingVideoFile(file);
    setPendingVideoPreview(URL.createObjectURL(file));
    if (dvFileRef.current) dvFileRef.current.value = '';
  };

  const handleDefaultVideoSave = async () => {
    if (!pendingVideoFile) return;
    setDvLoading(true);
    const formData = new FormData();
    formData.append('file', pendingVideoFile);
    try {
      const res = await fetch(`${API}/api/admin/sponsor/default-video`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (res.ok) {
        const d = await res.json();
        setDefaultPromo(prev => ({ ...prev, video_url: d.video_url }));
        toast.success('Default sponsor video saved successfully');
        // Clean up local preview
        if (pendingVideoPreview) URL.revokeObjectURL(pendingVideoPreview);
        setPendingVideoFile(null);
        setPendingVideoPreview(null);
      } else {
        const d = await res.json().catch(() => ({}));
        toast.error(d.detail || 'Upload failed');
      }
    } catch { toast.error('Upload failed'); }
    setDvLoading(false);
  };

  const handleDefaultVideoReplace = (e) => {
    handleDefaultVideoSelect(e);
  };

  const handleCancelPendingVideo = () => {
    if (pendingVideoPreview) URL.revokeObjectURL(pendingVideoPreview);
    setPendingVideoFile(null);
    setPendingVideoPreview(null);
  };

  const handleDefaultVideoRemove = async () => {
    setDvLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/sponsor/default-video`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) { setDefaultPromo(prev => ({ ...prev, video_url: null })); toast.success('Default video removed'); }
    } catch { toast.error('Remove failed'); }
    setDvLoading(false);
  };

  const handleDefaultMetaSave = async () => {
    setMetaSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/sponsor/default-metadata`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sponsor_name: defaultPromo.sponsor_name,
          headline: defaultPromo.headline,
          description: defaultPromo.description,
          cta_text: defaultPromo.cta_text,
          website_url: defaultPromo.website_url,
          contact_info: defaultPromo.contact_info,
        }),
      });
      if (res.ok) {
        toast.success('Default metadata saved');
      } else {
        const d = await res.json().catch(() => ({}));
        toast.error(d.detail || 'Save failed');
      }
    } catch { toast.error('Save failed'); }
    setMetaSaving(false);
  };

  const updateDefaultField = (field, value) => {
    setDefaultPromo(prev => ({ ...prev, [field]: value }));
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const pending = subs.filter(s => s.status === 'pending').length;

  return (
    <div data-testid="sponsor-admin">
      <h2 className="text-white font-bold text-lg mb-1 flex items-center gap-2">
        <Megaphone size={18} className="text-[#E91E8D]" /> Sponsor of the Day
      </h2>
      <p className="text-white/30 text-xs mb-5">Review, approve, and schedule sponsor advertisements.</p>

      {/* Default Sponsor Video + Metadata */}
      <div className="glass-card px-4 py-4 mb-4" data-testid="default-video-section">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Video size={12} className="text-[#E91E8D]" />
            <span className="text-white/50 text-[10px] font-bold uppercase tracking-wider">Default Promo Video &amp; Metadata</span>
          </div>
          {dvLoading && <Loader2 size={12} className="text-[#E91E8D] animate-spin" />}
        </div>

        {/* Video upload — two-step: select → preview → SAVE VIDEO */}
        {pendingVideoPreview ? (
          <div className="space-y-2 mb-4">
            <p className="text-amber-400/60 text-[9px] font-bold uppercase tracking-wider">New video selected — click Save Video to apply</p>
            <video
              src={pendingVideoPreview}
              className="w-full max-h-40 rounded-lg object-contain bg-black/20"
              controls muted
              data-testid="pending-video-preview"
            />
            <div className="flex gap-2">
              <button
                onClick={handleDefaultVideoSave}
                disabled={dvLoading}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg border bg-green-400/10 border-green-400/20 text-green-400 hover:bg-green-400/20 text-[10px] font-bold uppercase tracking-wider transition-colors disabled:opacity-40"
                data-testid="save-default-video-btn"
              >
                {dvLoading ? <Loader2 size={10} className="animate-spin" /> : <Check size={10} />} Save Video
              </button>
              <button
                onClick={handleCancelPendingVideo}
                disabled={dvLoading}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg border bg-white/5 border-white/10 text-white/40 hover:bg-white/10 text-[10px] font-semibold transition-colors disabled:opacity-40"
                data-testid="cancel-pending-video-btn"
              >
                <X size={10} /> Cancel
              </button>
            </div>
          </div>
        ) : defaultPromo.video_url ? (
          <div className="space-y-2 mb-4">
            <video
              src={defaultPromo.video_url}
              className="w-full max-h-40 rounded-lg object-contain bg-black/20"
              controls muted
              data-testid="default-video-preview"
            />
            <div className="flex gap-2">
              <button
                onClick={() => dvFileRef.current?.click()}
                disabled={dvLoading}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border bg-[#E91E8D]/5 border-[#E91E8D]/10 text-[#E91E8D] hover:bg-[#E91E8D]/10 text-[10px] font-semibold transition-colors disabled:opacity-40"
                data-testid="replace-default-video-btn"
              >
                <Upload size={10} /> Replace
              </button>
              <button
                onClick={handleDefaultVideoRemove}
                disabled={dvLoading}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border bg-red-400/5 border-red-400/10 text-red-400 hover:bg-red-400/10 text-[10px] font-semibold transition-colors disabled:opacity-40"
                data-testid="remove-default-video-btn"
              >
                <Trash2 size={10} /> Remove
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => dvFileRef.current?.click()}
            disabled={dvLoading}
            className="w-full border-2 border-dashed border-white/10 rounded-lg py-4 text-center cursor-pointer hover:border-[#E91E8D]/30 transition-colors disabled:opacity-40 mb-4"
            data-testid="upload-default-video-btn"
          >
            <Upload size={14} className="text-white/20 mx-auto mb-1" />
            <p className="text-white/30 text-[10px]">Upload default promo video</p>
            <p className="text-white/15 text-[9px]">Shown when no active sponsor</p>
          </button>
        )}
        <input
          ref={dvFileRef}
          type="file"
          accept="video/mp4,video/webm,video/quicktime"
          onChange={handleDefaultVideoSelect}
          className="hidden"
          data-testid="default-video-file-input"
        />

        {/* Default metadata fields */}
        <div className="border-t border-white/5 pt-3 space-y-3">
          <p className="text-white/30 text-[9px] font-bold uppercase tracking-wider">Default Promo Metadata</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <MetaField label="Sponsor / Business Name" value={defaultPromo.sponsor_name} onChange={v => updateDefaultField('sponsor_name', v)} testId="default-meta-name" />
            <MetaField label="Headline" value={defaultPromo.headline} onChange={v => updateDefaultField('headline', v)} testId="default-meta-headline" />
          </div>
          <MetaField label="Description" value={defaultPromo.description} onChange={v => updateDefaultField('description', v)} multiline testId="default-meta-description" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <MetaField label="CTA Text" value={defaultPromo.cta_text} onChange={v => updateDefaultField('cta_text', v)} placeholder="e.g. SPONSOR THIS ROOM" testId="default-meta-cta" />
            <MetaField label="Website URL" value={defaultPromo.website_url} onChange={v => updateDefaultField('website_url', v)} placeholder="https://..." testId="default-meta-url" />
          </div>
          <MetaField label="Contact (optional)" value={defaultPromo.contact_info} onChange={v => updateDefaultField('contact_info', v)} placeholder="email, phone, or social" testId="default-meta-contact" />
          <button
            onClick={handleDefaultMetaSave}
            disabled={metaSaving}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#E91E8D]/10 border border-[#E91E8D]/20 text-[#E91E8D] hover:bg-[#E91E8D]/20 text-[10px] font-semibold transition-colors disabled:opacity-40"
            data-testid="save-default-meta-btn"
          >
            {metaSaving ? <Loader2 size={10} className="animate-spin" /> : <Check size={10} />} Save Metadata
          </button>
        </div>

        {/* Default promo preview */}
        {(defaultPromo.video_url || defaultPromo.sponsor_name || defaultPromo.headline) && (
          <div className="border-t border-white/5 pt-3 mt-3">
            <p className="text-white/20 text-[9px] font-bold uppercase mb-2">Public Preview</p>
            <div className="rounded-xl border border-white/5 bg-white/[0.02] overflow-hidden" data-testid="default-promo-preview">
              {defaultPromo.video_url && (
                <video src={defaultPromo.video_url} className="w-full max-h-48 object-contain" controls muted />
              )}
              <SponsorInfo sponsor={defaultPromo} isDefault />
            </div>
          </div>
        )}
      </div>

      {/* Booked Dates Calendar */}
      <div className="glass-card px-4 py-4 mb-4" data-testid="sponsor-calendar-section">
        <div className="flex items-center gap-2 mb-3">
          <Calendar size={12} className="text-[#E91E8D]" />
          <span className="text-white/50 text-[10px] font-bold uppercase tracking-wider">Sponsor Calendar</span>
        </div>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-shrink-0" data-testid="sponsor-calendar">
            <CalendarUI
              mode="single"
              month={calMonth}
              onMonthChange={setCalMonth}
              modifiers={{
                booked_pending: bookedDates.filter(d => d.status === 'pending').map(d => new Date(d.date || d.start)),
                booked_approved: bookedDates.filter(d => d.status === 'approved').map(d => new Date(d.date || d.start)),
                booked_published: bookedDates.filter(d => d.status === 'published').map(d => new Date(d.date || d.start)),
              }}
              modifiersStyles={{
                booked_pending: { backgroundColor: 'rgba(251,191,36,0.2)', color: '#fbbf24', fontWeight: 700, borderRadius: '50%' },
                booked_approved: { backgroundColor: 'rgba(96,165,250,0.2)', color: '#60a5fa', fontWeight: 700, borderRadius: '50%' },
                booked_published: { backgroundColor: 'rgba(52,211,153,0.2)', color: '#34d399', fontWeight: 700, borderRadius: '50%' },
              }}
              className="!bg-transparent [&_.rdp-caption_label]:text-white/60 [&_.rdp-head_cell]:text-white/20 [&_.rdp-day]:text-white/40 [&_.rdp-day_today]:bg-white/5 [&_.rdp-day_today]:text-white [&_.rdp-nav_button]:text-white/30 [&_.rdp-nav_button:hover]:text-white/60 [&_.rdp-nav_button]:bg-transparent [&_.rdp-nav_button]:border-white/10"
            />
          </div>
          <div className="flex-1 min-w-0 space-y-1.5">
            <p className="text-white/25 text-[9px] font-bold uppercase tracking-wider mb-2">Legend</p>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-amber-400/30 border border-amber-400/50" />
              <span className="text-white/40 text-[10px]">Pending Review</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-blue-400/30 border border-blue-400/50" />
              <span className="text-white/40 text-[10px]">Approved</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-green-400/30 border border-green-400/50" />
              <span className="text-white/40 text-[10px]">Published / Live</span>
            </div>
            {bookedDates.length > 0 ? (
              <div className="mt-3 space-y-1">
                <p className="text-white/25 text-[9px] font-bold uppercase tracking-wider">Booked Dates</p>
                {bookedDates.map((d, i) => {
                  const dateStr = d.date || d.start;
                  const statusColor = d.status === 'published' ? 'text-green-400' : d.status === 'approved' ? 'text-blue-400' : 'text-amber-400';
                  return (
                    <div key={i} className="flex items-center gap-2 text-[10px]">
                      <span className="text-white/30">{dateStr}</span>
                      <span className={`${statusColor} font-semibold capitalize`}>{d.status}</span>
                      <span className="text-white/20 truncate">{d.sponsor_name}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-white/15 text-[10px] mt-3">No dates booked yet.</p>
            )}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="flex gap-3 mb-4">
        <div className="glass-card px-3 py-2 flex-1">
          <p className="text-white/30 text-[9px] font-bold uppercase tracking-wider">Total</p>
          <p className="text-white text-lg font-bold">{subs.length}</p>
        </div>
        <div className="glass-card px-3 py-2 flex-1">
          <p className="text-amber-400/60 text-[9px] font-bold uppercase tracking-wider">Pending</p>
          <p className="text-amber-400 text-lg font-bold">{pending}</p>
        </div>
        <div className="glass-card px-3 py-2 flex-1">
          <p className="text-green-400/60 text-[9px] font-bold uppercase tracking-wider">Published</p>
          <p className="text-green-400 text-lg font-bold">{subs.filter(s => s.status === 'published').length}</p>
        </div>
      </div>

      {loading ? (
        <div className="py-6 text-center"><Loader2 size={16} className="text-[#E91E8D] animate-spin mx-auto" /></div>
      ) : subs.length === 0 ? (
        <div className="glass-card px-4 py-8 text-center"><p className="text-white/20 text-xs">No sponsor submissions yet</p></div>
      ) : (
        <div className="space-y-2">
          {subs.map(s => (
            <div key={s.id}>
              <div className={`glass-card px-4 py-3 cursor-pointer transition-all hover:border-white/10 ${expanded === s.id ? 'border-[#E91E8D]/20' : ''}`}
                onClick={() => setExpanded(expanded === s.id ? null : s.id)} data-testid={`sponsor-row-${s.id}`}>
                <div className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-white text-xs font-medium truncate">{s.sponsor_name || 'Untitled'}</p>
                      {s.creative_assistance_required && <Palette size={10} className="text-[#E91E8D] flex-shrink-0" title="Creative assistance" />}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-white/30 text-[10px]">${(s.amount_cents || 15000) / 100}</span>
                      <span className="text-white/15 text-[10px]">{formatDate(s.created_at)}</span>
                      {s.requested_date && <span className="text-white/20 text-[10px] flex items-center gap-0.5"><Calendar size={8} /> {s.requested_date}</span>}
                    </div>
                  </div>
                  <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase ${STATUS_COLORS[s.status] || 'bg-white/5 text-white/30'}`}>{s.status}</span>
                  <ChevronDown size={12} className={`text-white/20 transition-transform ${expanded === s.id ? 'rotate-180' : ''}`} />
                </div>
              </div>

              {expanded === s.id && (
                <div className="mx-2 mt-1 mb-2 rounded-xl border border-white/5 bg-white/[0.015] p-4 space-y-4" data-testid={`sponsor-detail-${s.id}`}>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Sponsor" value={s.sponsor_name} />
                    <Field label="Headline" value={s.headline} />
                    <Field label="Website" value={s.website_url} link />
                    <Field label="CTA" value={s.cta_text} />
                    <Field label="Contact" value={s.contact_info} />
                    <Field label="Contact Public" value={s.display_contact_publicly ? 'Yes' : 'No'} />
                    <Field label="Creative Assist" value={s.creative_assistance_required ? 'Yes' : 'No'} />
                    <Field label="Square Order" value={s.square_order_id} mono />
                    <Field label="Payment" value={s.paid ? `Paid ($${(s.amount_cents || 15000) / 100})` : 'Unpaid'} />
                    <Field label="Requested Date" value={s.requested_date} />
                    <Field label="Status" value={s.status} />
                  </div>

                  {s.description && <div><p className="text-white/20 text-[9px] font-bold uppercase mb-1">Description</p><p className="text-white/50 text-xs">{s.description}</p></div>}
                  {s.campaign_description && <div><p className="text-white/20 text-[9px] font-bold uppercase mb-1">Campaign</p><p className="text-white/50 text-xs">{s.campaign_description}</p></div>}
                  {s.creative_notes && <div><p className="text-white/20 text-[9px] font-bold uppercase mb-1">Creative Notes</p><p className="text-white/50 text-xs">{s.creative_notes}</p></div>}

                  {s.media_url && (
                    <div>
                      <p className="text-white/20 text-[9px] font-bold uppercase mb-1">Media</p>
                      {s.media_type === 'video' ? (
                        <video src={s.media_url} className="w-full max-h-48 rounded-lg object-contain" controls muted />
                      ) : (
                        <img src={s.media_url} className="w-full max-h-48 rounded-lg object-contain" alt="Sponsor" />
                      )}
                    </div>
                  )}

                  {/* Public Preview */}
                  <div className="border-t border-white/5 pt-3">
                    <p className="text-white/20 text-[9px] font-bold uppercase mb-2">Public Preview</p>
                    <div className="rounded-xl border border-white/5 bg-white/[0.02] overflow-hidden" data-testid={`sponsor-preview-${s.id}`}>
                      {s.media_url && (
                        s.media_type === 'video' ? (
                          <video src={s.media_url} className="w-full max-h-48 object-contain" controls muted />
                        ) : (
                          <img src={s.media_url} className="w-full max-h-48 object-contain" alt="Preview" />
                        )
                      )}
                      <SponsorInfo sponsor={s} />
                    </div>
                  </div>

                  {['pending', 'approved'].includes(s.status) && (
                    <div className="border-t border-white/5 pt-3">
                      <p className="text-white/30 text-[9px] font-bold uppercase mb-2">Schedule & Publish</p>
                      <div className="grid grid-cols-2 gap-2 mb-3">
                        <input type="datetime-local" value={schedStart} onChange={e => setSchedStart(e.target.value)}
                          className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-xs [color-scheme:dark]" placeholder="Start" />
                        <input type="datetime-local" value={schedEnd} onChange={e => setSchedEnd(e.target.value)}
                          className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-xs [color-scheme:dark]" placeholder="End" />
                      </div>
                    </div>
                  )}

                  {s.status === 'published' && s.scheduled_start_at && (
                    <div className="border-t border-white/5 pt-3">
                      <p className="text-white/30 text-[9px] font-bold uppercase mb-1">Schedule</p>
                      <p className="text-white/50 text-xs">{formatDate(s.scheduled_start_at)} → {formatDate(s.scheduled_end_at)}</p>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2 pt-2 border-t border-white/5">
                    {s.status === 'pending' && (
                      <>
                        <ActionBtn icon={Check} label="Approve" color="green" onClick={() => action(s.id, 'approve')} />
                        <ActionBtn icon={X} label="Reject" color="red" onClick={() => action(s.id, 'reject')} />
                      </>
                    )}
                    {['pending', 'approved'].includes(s.status) && (
                      <ActionBtn icon={Eye} label="Publish" color="pink" onClick={() => handlePublish(s.id)} />
                    )}
                    {s.status === 'published' && (
                      <ActionBtn icon={EyeOff} label="Unpublish" color="amber" onClick={() => action(s.id, 'unpublish')} />
                    )}
                    {s.status !== 'archived' && (
                      <ActionBtn icon={Archive} label="Archive" color="gray" onClick={() => action(s.id, 'archive')} />
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, mono, link }) => (
  <div>
    <p className="text-white/20 text-[9px] font-bold uppercase tracking-wider">{label}</p>
    {link && value ? (
      <a href={value} target="_blank" rel="noopener noreferrer" className="text-[#E91E8D] text-[11px] hover:underline truncate block">{value}</a>
    ) : (
      <p className={`text-white/50 text-[11px] mt-0.5 truncate ${mono ? 'font-mono text-[10px]' : ''}`}>{value || '—'}</p>
    )}
  </div>
);

const ActionBtn = ({ icon: Icon, label, color, onClick }) => {
  const colors = {
    green: 'bg-green-400/5 border-green-400/10 text-green-400 hover:bg-green-400/10',
    red: 'bg-red-400/5 border-red-400/10 text-red-400 hover:bg-red-400/10',
    pink: 'bg-[#E91E8D]/5 border-[#E91E8D]/10 text-[#E91E8D] hover:bg-[#E91E8D]/10',
    amber: 'bg-amber-400/5 border-amber-400/10 text-amber-400 hover:bg-amber-400/10',
    gray: 'bg-white/5 border-white/10 text-white/30 hover:bg-white/10',
  };
  return (
    <button onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-[10px] font-semibold transition-colors ${colors[color]}`}>
      <Icon size={10} /> {label}
    </button>
  );
};

const MetaField = ({ label, value, onChange, multiline, placeholder, testId }) => (
  <div>
    <label className="block text-white/25 text-[9px] font-bold uppercase tracking-wider mb-1">{label}</label>
    {multiline ? (
      <textarea
        value={value || ''} onChange={e => onChange(e.target.value)}
        placeholder={placeholder || ''} rows={2}
        className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-[11px] placeholder-white/15 focus:outline-none focus:border-[#E91E8D]/30 resize-none"
        data-testid={testId}
      />
    ) : (
      <input
        type="text" value={value || ''} onChange={e => onChange(e.target.value)}
        placeholder={placeholder || ''}
        className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-[11px] placeholder-white/15 focus:outline-none focus:border-[#E91E8D]/30"
        data-testid={testId}
      />
    )}
  </div>
);

export default SponsorAdmin;
